import { UTM_ACT_KEY } from "@/const/storage-key";
import { getInsets, isInApp } from "@/libs/comeAppSDK";

export const isBrowser = () => typeof window !== 'undefined';

export const getUserAgent = () => {
  return isBrowser() ? window.navigator.userAgent : '';
};

export const isMobile = () => {
  if (!isBrowser()) return false;
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * 获取设备类型
 * @returns 'android' | 'ios' | 'pc' | 'other'
 */
export const getDeviceType = (): 'android' | 'ios' | 'pc' | 'other' => {
  if (!isBrowser()) return 'other';
  if (typeof navigator === 'undefined') return 'other';

  const ua = navigator.userAgent || navigator.vendor;

  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Windows|Macintosh|Linux/i.test(ua)) return 'pc';
  return 'other';
};

export const isIOS = () => {
  return getDeviceType() === 'ios';
};

export const isAndroid = () => {
  return getDeviceType() === 'android';
};

// 缓存安全区域信息（页面刷新时会重新初始化）
let cachedSafeInsets: { topSafe: number; bottomSafe: number; } | null = null;

/**
 * app内获取安全区域
 * 获取后会缓存到临时变量，页面刷新时重新获取
 * @returns { topSafe: number; bottomSafe: number; }
 */
export const getSafeInsets = async () => {
  if (!isInApp()) return { topSafe: 0, bottomSafe: 0 };

  // 检查是否有缓存
  if (cachedSafeInsets !== null) {
    return cachedSafeInsets;
  }

  // 没有缓存，重新获取
  try {
    const insets = await getInsets();
    if (!insets) return { topSafe: 0, bottomSafe: 0 };

    const result = { topSafe: insets.topSafe || 0, bottomSafe: insets.bottomSafe || 0 };

    // 存储到临时变量
    cachedSafeInsets = result;

    return result;
  } catch (error) {
    console.error('Failed to get safe insets:', error);
    // 如果获取安全区域失败，返回默认值
    return { topSafe: 0, bottomSafe: 0 };
  }
};

/**
 * 判断是否是apk的无游客包
 */
export const checkIsNoVisitorInApp = (ua?: string): boolean => {
  if (!isBrowser()) return false;
  return /fission/i.test(ua || window.navigator.userAgent);
};

/**
 * 判断是不是谷歌包App
 */
export const checkIsGoogleApp = (ua?: string): boolean => {
  if (!isBrowser()) return false;
  if (!isInApp()) { return false; }
  // ComeApp/normal/1.0.154-fec85c0-qa/com.edch.ykx
  try {
    const UA_PATTERN = /ComeApp\/(?<channel>[^/]+)\/(?<version>[^/]+)\/(?<packageName>[^/]+)/;
    // 使用
    const match = (ua || window.navigator.userAgent).match(UA_PATTERN);
    if (match?.groups) {
      const { version } = match.groups;
      return /google/i.test(version);
    }
  } catch (e) {
    console.error(e);
  }

  return false;
};

/**
 * 判断是否是safari
 */
export function isSafariBrowser() {
  if (!isBrowser()) return false;
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;

  const isInSafari =
    /Safari/.test(ua) &&
    !/Chrome/.test(ua) &&
    !/Chromium/.test(ua) &&
    !/Android/.test(ua);

  return isInSafari;
}


/**
 * 判断当前环境是否为PWA
 */
export function isInPWA(): boolean {
  if (isInApp()) return false;

  if (typeof window === 'undefined') return false;

  // 标准方式（Chrome / Android / Desktop / iOS 13+）
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // iOS Safari 专用（兼容老版本）
  if ((navigator as any).standalone === true) {
    return true;
  }

  return false;
}

// 优先从 URL 获取 utm_act(自裂变归因参数)，没有再从 utmActAtom 获取，最后尝试从 localStorage 读取并解析
export const getUtmActFromStorage = (): string => {
  try {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(UTM_ACT_KEY) : null;
    if (!stored) return '';
    // 尝试解析 JSON（atomWithStorage 可能存储为 JSON 字符串）
    try {
      const parsed = JSON.parse(stored);
      return typeof parsed === 'string' ? parsed : '';
    } catch {
      // 如果不是 JSON 格式，直接返回原始字符串
      return stored;
    }
  } catch {
    return '';
  }
};